import * as React from 'react';

import { useSpecializationReviewsExperimentConfig } from 'bundles/unified-description-page-common/providers/specialization/SpecializationReviewsExperimentProvider';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import {
  MOCK_RATING_COUNTS_BY_STAR,
  getCourseIdToNameMap,
  getMockSpecializationTopReviews,
} from 'bundles/unified-description-page-common/utils/getMockSpecializationReviews';

type SpecializationPageData = ConsumerSpecializationPageFieldsFragment | EnterpriseSpecializationPageFieldsFragment;

const MIN_MOCK_LOADING_DELAY_MS = 1000;
const MAX_MOCK_LOADING_DELAY_MS = 3000;

/**
 * Data-fetching hook for the specialization reviews module. Forked from the course reviews
 * renderer's plain pageData read because specialization topReviews/ratingCountsByStar aren't
 * real API fields yet (MERCH-3262) -- this mocks them onto the page data until they are, gated
 * behind the `specialization-reviews` Statsig experiment's `enableReviews` param.
 *
 * Mock reviews are already available synchronously on first render (as if fetched via SSR), so
 * `isLoading` only simulates a fetch delay when `selectedCourseId` changes after mount -- never on
 * initial render, and never for the control group (showReviews=false), which will never have
 * reviews to show.
 */
export const useSpecializationReviewsPageData = (
  pageData: SpecializationPageData | null | undefined,
  selectedCourseId?: string
) => {
  const { showReviews } = useSpecializationReviewsExperimentConfig();

  const isInitialRender = React.useRef(true);
  const [isLoading, setIsLoading] = React.useState(false);
  React.useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return undefined;
    }
    if (!showReviews) {
      return undefined;
    }
    setIsLoading(true);
    const delayMs = MIN_MOCK_LOADING_DELAY_MS + Math.random() * (MAX_MOCK_LOADING_DELAY_MS - MIN_MOCK_LOADING_DELAY_MS);
    const timeoutId = setTimeout(() => setIsLoading(false), delayMs);
    return () => clearTimeout(timeoutId);
  }, [selectedCourseId, showReviews]);

  const data = React.useMemo(() => {
    if (!showReviews || !pageData) {
      return {
        ...pageData,
        topReviews: null,
        ratingCountsByStar: null,
        courseIdToNameMap: new Map<string, string>(),
      };
    }

    return {
      ...pageData,
      topReviews: getMockSpecializationTopReviews(pageData.courses, selectedCourseId),
      ratingCountsByStar: MOCK_RATING_COUNTS_BY_STAR,
      courseIdToNameMap: getCourseIdToNameMap(pageData.courses),
    };
  }, [showReviews, pageData, selectedCourseId]);

  return { ...data, isLoading };
};
